import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button } from "react-bootstrap";
import { FaPhone, FaEnvelope, FaEdit, FaTrash } from "react-icons/fa";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
} from "@syncfusion/ej2-react-grids";

import Spinner from "../../Components/Spinner";
import DeleteModal from "../../Components/Modals/Delete-modal";
import SearchInputDocument from "../../Components/Forms/SearchInputDocument";
import EmptyData from "../../../components/EmptyData";
import { ToastComponent } from "../../Components/ToastComponent";
import { contactsService } from "../../../Services/contacts.service";
import { errorHandling } from "../../../Services/error.handling";
import { formatPhoneNumber } from "../../../Services/string.service";

const emptyForm = { contactId: null, name: "", doc: "", relationshipId: "", phone: "", email: "" };

// Editar/excluir o contato em si (nome/doc/vínculo) usa updateContact/
// deleteContact do contacts.service.js. Quando formData.contactId está
// preenchido, o form vira modo edição (telefone/e-mail ficam desabilitados,
// pois são geridos nos modais próprios).
export default function ContactsList({ clientId }) {
  const { t } = useTranslation();

  const [contacts, setContacts] = useState([]);
  const [phoneTypes, setPhoneTypes] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState(false);

  const [formData, setFormData] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});

  const [contactPhones, setContactPhones] = useState({});
  const [contactEmails, setContactEmails] = useState({});

  const [phoneModalContact, setPhoneModalContact] = useState(null);
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [phoneTypeInput, setPhoneTypeInput] = useState("");
  const [isLoadingPhoneModal, setIsLoadingPhoneModal] = useState(false);

  const [emailModalContact, setEmailModalContact] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [isLoadingEmailModal, setIsLoadingEmailModal] = useState(false);

  const [toastParams, setToastParams] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const asList = (response) =>
    Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [];

  // GET /contacts/clients/{clientId} — o id do contato às vezes vem como
  // contactId/ContactId, às vezes só como id/Id (visto em payload real onde
  // contactId chegava undefined e sumia do JSON enviado pro POST de
  // telefone/e-mail). Cobre os dois formatos.
  const normalizeContact = (c) => ({
    contactId: c.contactId ?? c.ContactId ?? c.id ?? c.Id,
    name: c.name ?? c.Name,
    doc: c.doc ?? c.Doc,
    relationshipId: c.relationshipId ?? c.RelationshipId,
  });

  // Chama a listagem de novo — ao entrar na aba (no useEffect abaixo) e
  // depois de criar/editar/deletar um contato, em vez de só remendar o
  // estado local, que podia ficar desatualizado em relação ao que foi
  // realmente salvo (mesmo padrão de refreshPhones/refreshEmails).
  const refreshContacts = async () => {
    setIsLoadingContacts(true);
    setContactsError(false);

    try {
      const response = await contactsService.fetchByClient(clientId);
      if (response === null) {
        setContactsError(true);
        setContacts([]);
        return;
      }
      setContacts(asList(response).map(normalizeContact));
    } catch (error) {
      setContactsError(true);
      setContacts([]);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      setIsLoadingContacts(true);
      setContactsError(false);

      try {
        const [contactsResponse, phoneTypesResponse, relationshipsResponse] = await Promise.all([
          contactsService.fetchByClient(clientId),
          contactsService.fetchPhoneTypes(),
          contactsService.fetchRelationships(),
        ]);

        if (!isMounted) return;

        if (contactsResponse === null) {
          setContactsError(true);
          setContacts([]);
        } else {
          setContacts(asList(contactsResponse).map(normalizeContact));
        }
        setPhoneTypes(asList(phoneTypesResponse));
        setRelationships(asList(relationshipsResponse));
      } catch (error) {
        if (isMounted) {
          setContactsError(true);
          setContacts([]);
        }
      } finally {
        if (isMounted) setIsLoadingContacts(false);
      }
    };

    loadInitialData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const relationshipName = (relationshipId) => {
    const found = relationships.find(
      (relationship) => (relationship.id ?? relationship.Id) === relationshipId
    );
    return found?.description ?? found?.Description ?? t("CUSTOMER.CONTACTS_LIST.RELATIONSHIP_UNKNOWN");
  };

  const phoneTypeName = (phoneTypeId) => {
    const found = phoneTypes.find(
      (phoneType) => (phoneType.id ?? phoneType.phoneTypeId ?? phoneType.PhoneTypeId) === phoneTypeId
    );
    return found?.description ?? found?.name ?? found?.Name ?? "";
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleClearForm = () => {
    setFormData(emptyForm);
    setFieldErrors({});
  };

  const handleEditContact = (contact) => {
    setFormData({
      contactId: contact.contactId,
      name: contact.name ?? "",
      doc: contact.doc ?? "",
      relationshipId: contact.relationshipId ?? "",
      phone: "",
      email: "",
    });
    setFieldErrors({});
  };

  const [contactToDelete, setContactToDelete] = useState(null);

  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
  };

  const confirmDeleteContact = async (contactId) => {
    const response = await contactsService.deleteContact(clientId, contactId);


    if (response?.error) {
      showToast(
        t("COMMON.ERROR"),
        response?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.SAVE_CONTACT_ERROR"),
        "Danger"
      );
      return;
    }

    if (formData.contactId === contactId) {
      setFormData(emptyForm);
    }
    showToast(t("COMMON.SUCCESS"), t("CUSTOMER.CONTACTS_LIST.CONTACT_DELETED"), "Success");
    await refreshContacts();
  };

  const handleSaveContact = async (e) => {
    e?.preventDefault?.();

    const isEditing = Boolean(formData.contactId);
    const errors = {};
    const documentDigits = formData.doc.replace(/\D/g, "");
    const phoneDigits = formData.phone.replace(/\D/g, "");

    if (!formData.name.trim()) errors.name = true;
    if (!documentDigits || ![11, 14].includes(documentDigits.length)) errors.doc = true;
    if (!formData.relationshipId) errors.relationshipId = true;

    if (!isEditing && (!phoneDigits || phoneDigits.length < 10)) {
      errors.phone = true;
    }

    if (!isEditing && (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))) {
      errors.email = true;
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      showToast(t("COMMON.ATTENTION"), t("CUSTOMER.CONTACTS_LIST.REQUIRED_FIELDS_ERROR"), "Warning");
      return;
    }

    setIsSaving(true);
    try {
      const contactResponse = isEditing
        ? await contactsService.updateContact(formData.contactId, {
            name: formData.name.trim(),
            doc: formData.doc.trim(),
            relationshipId: formData.relationshipId,
          })
        : await contactsService.createContact({
            name: formData.name.trim(),
            doc: formData.doc.trim(),
            relationshipId: formData.relationshipId,
          });

      if (!contactResponse || contactResponse.error) {
        showToast(
          t("COMMON.ERROR"),
          errorHandling.getErrorMessage(contactResponse, t("CUSTOMER.CONTACTS_LIST.SAVE_CONTACT_ERROR")),
          "Danger"
        );
        return;
      }

      if (isEditing) {
        showToast(t("COMMON.SUCCESS"), t("CUSTOMER.CONTACTS_LIST.CONTACT_UPDATED"), "Success");
        setFormData(emptyForm);
        setFieldErrors({});
        await refreshContacts();
        return;
      }

      const newContactId =
    typeof contactResponse === "number"
    ? contactResponse
    : contactResponse.contactId ??
      contactResponse.ContactId ??
      contactResponse.id ??
      contactResponse.Id ??
      contactResponse.data?.contactId ??
      contactResponse.data?.ContactId;

      if (formData.phone.trim()) {
        const defaultPhoneType = phoneTypes[0]?.id ?? phoneTypes[0]?.phoneTypeId ?? phoneTypes[0]?.PhoneTypeId ?? "";
        const phoneResponse = await contactsService.createPhone({
          contactId: newContactId,
          phone: formData.phone.replace(/\D/g, ""),
          extension: "",
          phoneTypeId: defaultPhoneType,
        });
        if (phoneResponse && !phoneResponse.error) {
          setContactPhones((prev) => ({
            ...prev,
            [newContactId]: [
              {
                id: phoneResponse.contactPhoneId ?? phoneResponse.ContactPhoneId,
                number: formData.phone.replace(/\D/g, ""),
                extension: "",
                phoneTypeId: defaultPhoneType,
              },
            ],
          }));
        }
      }

      if (formData.email.trim()) {
        const emailResponse = await contactsService.createEmail({
          contactId: newContactId,
          email: formData.email.trim(),
        });
        if (emailResponse && !emailResponse.error) {
          setContactEmails((prev) => ({
            ...prev,
            [newContactId]: [
              { id: emailResponse.contactEmailId ?? emailResponse.ContactEmailId, email: formData.email.trim() },
            ],
          }));
        }
      }

      showToast(t("COMMON.SUCCESS"), t("CUSTOMER.CONTACTS_LIST.CONTACT_CREATED"), "Success");
      setFormData(emptyForm);
      setFieldErrors({});
      await refreshContacts();
    } finally {
      setIsSaving(false);
    }
  };

  // Sempre busca do backend de novo — tanto ao abrir o modal quanto depois
  // de criar um telefone novo — em vez de confiar só no estado local
  // acumulado, que podia ficar desatualizado em relação ao que foi
  // realmente salvo.
  const refreshPhones = async (contactId) => {
    setIsLoadingPhoneModal(true);
    try {
      const response = await contactsService.fetchPhones(contactId);
      const normalized = asList(response).map((p) => ({
        id: p.id ?? p.Id,
        number: p.number ?? p.Number,
        extension: p.extension ?? p.Extension,
        phoneTypeId: p.phoneTypeId ?? p.PhoneTypeId,
      }));
      setContactPhones((prev) => ({ ...prev, [contactId]: normalized }));
    } catch (error) {
      setContactPhones((prev) => ({ ...prev, [contactId]: [] }));
    } finally {
      setIsLoadingPhoneModal(false);
    }
  };

  const openPhoneModal = (contact) => {
    setPhoneModalContact(contact);
    setPhoneNumberInput("");
    setPhoneTypeInput("");
    refreshPhones(contact.contactId);
  };

  const closePhoneModal = () => setPhoneModalContact(null);

  const handleAddPhone = async () => {
    if (!phoneNumberInput.trim()) return;

    const contactId = phoneModalContact.contactId;
    const response = await contactsService.createPhone({
      contactId,
      phone: phoneNumberInput.replace(/\D/g, ""),
      extension: "",
      phoneTypeId: phoneTypeInput,
    });

    if (!response || response.error) {
      showToast(
        t("COMMON.ERROR"),
        errorHandling.getErrorMessage(response, t("CUSTOMER.CONTACTS_LIST.SAVE_PHONE_ERROR")),
        "Danger"
      );
      return;
    }

    setPhoneNumberInput("");
    setPhoneTypeInput("");
    await refreshPhones(contactId);
  };

  const handleDeletePhone = async (phoneId) => {
    const contactId = phoneModalContact.contactId;
    const response = await contactsService.deletePhone(phoneId);

    if (!response || response.error) {
      showToast(t("COMMON.ERROR"), response?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.DELETE_PHONE_ERROR"), "Danger");
      return;
    }

    await refreshPhones(contactId);
  };

  // Mesmo motivo do refreshPhones: busca do backend de novo ao abrir o
  // modal e depois de criar um e-mail novo, em vez de confiar no estado
  // local acumulado.
  const refreshEmails = async (contactId) => {
    setIsLoadingEmailModal(true);
    try {
      const response = await contactsService.fetchEmails(contactId);
      const normalized = asList(response).map((e) => ({
        id: e.id ?? e.Id,
        email: e.email ?? e.Email,
      }));
      setContactEmails((prev) => ({ ...prev, [contactId]: normalized }));
    } catch (error) {
      setContactEmails((prev) => ({ ...prev, [contactId]: [] }));
    } finally {
      setIsLoadingEmailModal(false);
    }
  };

  const openEmailModal = (contact) => {
    setEmailModalContact(contact);
    setEmailInput("");
    refreshEmails(contact.contactId);
  };

  const closeEmailModal = () => setEmailModalContact(null);

  const handleAddEmail = async () => {
    if (!emailInput.trim()) return;

    const contactId = emailModalContact.contactId;
    const response = await contactsService.createEmail({ contactId, email: emailInput.trim() });

    if (!response || response.error) {
      showToast(
        t("COMMON.ERROR"),
        errorHandling.getErrorMessage(response, t("CUSTOMER.CONTACTS_LIST.SAVE_EMAIL_ERROR")),
        "Danger"
      );
      return;
    }

    setEmailInput("");
    await refreshEmails(contactId);
  };

  const handleDeleteEmail = async (emailId) => {
    const contactId = emailModalContact.contactId;
    const response = await contactsService.deleteEmail(emailId);

    if (!response || response.error) {
      showToast(t("COMMON.ERROR"), response?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.DELETE_EMAIL_ERROR"), "Danger");
      return;
    }

    await refreshEmails(contactId);
  };

  const currentPhones = contactPhones[phoneModalContact?.contactId] ?? [];
  const currentEmails = contactEmails[emailModalContact?.contactId] ?? [];

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="bg-primary p-2 rounded mb-3">
        <strong style={{ color: "var(--text-primary)" }}>{t("CUSTOMER.CONTACTS_LIST.PAGE_TITLE")}</strong>
      </div>

      <p className="text-muted small">{t("CUSTOMER.CONTACTS_LIST.TRUST_HINT")}</p>

      <form onSubmit={handleSaveContact} className="mb-4">
        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <label className="form-label">{t("COMMON.NAME")}</label>
            <input
              type="text"
              className={`form-control${fieldErrors.name ? " is-invalid" : ""}`}
              value={formData.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              aria-invalid={fieldErrors.name || undefined}
            />
          </div>
          <div className="col-md-4">
            <SearchInputDocument
              name="contactDoc"
              label={t("CUSTOMER.CONTACTS_LIST.DOC_LABEL")}
              value={formData.doc}
              onChange={(e) => handleFormChange("doc", e.target.value)}
              labelPosition="top"
              isInvalid={fieldErrors.doc}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t("COMMON.TYPE")}</label>
            <select
              className={`form-select${fieldErrors.relationshipId ? " is-invalid" : ""}`}
              value={formData.relationshipId}
              onChange={(e) => handleFormChange("relationshipId", e.target.value)}
              aria-invalid={fieldErrors.relationshipId || undefined}
            >
              <option value="">{t("CUSTOMER.CONTACTS_LIST.SELECT_PLACEHOLDER")}</option>
              {relationships.map((relationship) => {
                const id = relationship.id ?? relationship.Id;
                const name = relationship.description ?? relationship.Description;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <label className="form-label">{t("CUSTOMER.CONTACTS_LIST.PHONE_LABEL")}</label>
            <input
              type="text"
              className={`form-control${fieldErrors.phone ? " is-invalid" : ""}`}
              value={formatPhoneNumber(formData.phone)}
              onChange={(e) => handleFormChange("phone", e.target.value)}
              disabled={Boolean(formData.contactId)}
              maxLength={15}
              aria-invalid={fieldErrors.phone || undefined}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">{t("CUSTOMER.CONTACTS_LIST.EMAIL_LABEL")}</label>
            <input
              type="email"
              className={`form-control${fieldErrors.email ? " is-invalid" : ""}`}
              value={formData.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              disabled={Boolean(formData.contactId)}
              aria-invalid={fieldErrors.email || undefined}
            />
          </div>
        </div>

       <button type="submit" className="btn btn-primary me-2" disabled={isSaving}>
          {t("COMMON.SAVE")}
        </button>
        <button type="button" className="btn btn-outline-primary" onClick={handleClearForm}>
          {formData.contactId ? t("BUTTONS.CANCEL") : t("CUSTOMER.CONTACTS_LIST.CLEAR")}
        </button>
      </form>

      <div className="bg-dark text-white px-3 py-2">
        <strong>{t("CUSTOMER.CONTACTS_LIST.REGISTERED_TITLE")}</strong>
      </div>

      {isLoadingContacts ? (
        <div className="p-3">
          <Spinner />
        </div>
      ) : contactsError ? (
        <p className="text-danger mt-3">{t("CUSTOMER.CONTACTS_LIST.LOAD_CONTACTS_ERROR")}</p>
      ) : contacts.length === 0 ? (
        <div className="mt-3">
          <EmptyData />
        </div>
      ) : (
        <>
          <GridComponent
            style={{ marginTop: 16 }}
            dataSource={contacts.map((contact) => ({
              ...contact,
              relationshipDisplay: relationshipName(contact.relationshipId),
            }))}
            height="auto"
          >
            <ColumnsDirective>
              <ColumnDirective field="name" headerText={t("COMMON.NAME")} width="200" />
              <ColumnDirective
                field="relationshipDisplay"
                headerText={t("COMMON.TYPE")}
                width="160"
              />
              <ColumnDirective
                headerText={t("CUSTOMER.CONTACTS_LIST.COLUMN_ACTIONS")}
                width="100"
                textAlign="Center"
                template={(row) => (
                  <div className="py-1">
                    <button
                      type="button"
                      className="btn btn-link text-primary p-1 me-1"
                      onClick={() => openPhoneModal(row)}
                      title={t("CUSTOMER.CONTACTS_LIST.PHONES_TITLE")}
                    >
                      <FaPhone />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link text-primary p-1 me-1"
                      onClick={() => openEmailModal(row)}
                      title={t("CUSTOMER.CONTACTS_LIST.EMAILS_TITLE")}
                    >
                      <FaEnvelope />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link text-primary p-1 me-1"
                      onClick={() => handleEditContact(row)}
                      title={t("CUSTOMER.CONTACTS_LIST.EDIT")}
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link text-danger p-1"
                      onClick={() => handleDeleteContact(row)}
                      title={t("CUSTOMER.CONTACTS_LIST.DELETE")}
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              />
            </ColumnsDirective>
          </GridComponent>
        </>
      )}

      <Modal show={Boolean(phoneModalContact)} onHide={closePhoneModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("CUSTOMER.CONTACTS_LIST.ADD_PHONES_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-6">
              <label className="form-label">{t("CUSTOMER.CONTACTS_LIST.PHONE_LABEL")}</label>
              <input
                type="text"
                className="form-control"
                value={formatPhoneNumber(phoneNumberInput)}
                onChange={(e) => setPhoneNumberInput(e.target.value)}
                maxLength={15}
              />
            </div>
            <div className="col-4">
              <label className="form-label">{t("COMMON.TYPE")}</label>
              <select
                className="form-select"
                value={phoneTypeInput}
                onChange={(e) => setPhoneTypeInput(e.target.value)}
              >
                <option value="">{t("CUSTOMER.CONTACTS_LIST.SELECT_PLACEHOLDER")}</option>
                {phoneTypes.map((phoneType) => {
                  const id = phoneType.id ?? phoneType.phoneTypeId ?? phoneType.PhoneTypeId;
                  const name = phoneType.description ?? phoneType.name ?? phoneType.Name;
                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-2">
              <button type="button" className="btn btn-primary w-100" onClick={handleAddPhone}>
                {t("COMMON.SAVE")}
              </button>
            </div>
          </div>

          {isLoadingPhoneModal ? (
            <Spinner />
          ) : (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t("CUSTOMER.CONTACTS_LIST.COLUMN_NUMBER")}</th>
                  <th>{t("CUSTOMER.CONTACTS_LIST.EXTENSION")}</th>
                  <th>{t("COMMON.TYPE")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {currentPhones.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      {t("CUSTOMER.CONTACTS_LIST.NO_PHONES")}
                    </td>
                  </tr>
                ) : (
                  currentPhones.map((phone) => (
                    <tr key={phone.id}>
                      <td>{phone.number}</td>
                      <td>{phone.extension}</td>
                      <td>{phoneTypeName(phone.phoneTypeId)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeletePhone(phone.id)}
                        >
                          {t("CUSTOMER.CONTACTS_LIST.REMOVE")}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePhoneModal}>
            {t("COMMON.CLOSE")}
          </Button>
        </Modal.Footer>
      </Modal>

      <DeleteModal
        show={Boolean(contactToDelete)}
        handleClose={() => setContactToDelete(null)}
        itemName={contactToDelete?.name}
        itemRemoveId={contactToDelete?.contactId}
        callBackRemove={confirmDeleteContact}
      />

      <Modal show={Boolean(emailModalContact)} onHide={closeEmailModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("CUSTOMER.CONTACTS_LIST.ADD_EMAILS_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-8">
              <label className="form-label">{t("CUSTOMER.CONTACTS_LIST.EMAIL_LABEL")}</label>
              <input
                type="text"
                className="form-control"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>
            <div className="col-4">
              <button type="button" className="btn btn-primary w-100" onClick={handleAddEmail}>
                {t("COMMON.SAVE")}
              </button>
            </div>
          </div>

          {isLoadingEmailModal ? (
            <Spinner />
          ) : (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t("CUSTOMER.CONTACTS_LIST.EMAIL_LABEL")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {currentEmails.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-muted">
                      {t("CUSTOMER.CONTACTS_LIST.NO_EMAILS")}
                    </td>
                  </tr>
                ) : (
                  currentEmails.map((email) => (
                    <tr key={email.id}>
                      <td>{email.email}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteEmail(email.id)}
                        >
                          {t("CUSTOMER.CONTACTS_LIST.REMOVE")}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEmailModal}>
            {t("COMMON.CLOSE")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
